import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

export type LandingVariant = 'original' | 'v2';

interface LandingVariantContextValue {
  variant: LandingVariant;
  setVariant: (variant: LandingVariant) => void;
  toggleVariant: () => void;
  isV2: boolean;
}

const LandingVariantContext = createContext<LandingVariantContextValue | undefined>(undefined);

const VARIANT_STORAGE_KEY = 'cwe-landing-variant';

function getInitialVariant(): LandingVariant {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(VARIANT_STORAGE_KEY);
    if (stored === 'original' || stored === 'v2') {
      return stored;
    }
  }
  return 'original';
}

interface LandingVariantProviderProps {
  children: React.ReactNode;
  defaultVariant?: LandingVariant;
}

export function LandingVariantProvider({ children, defaultVariant }: LandingVariantProviderProps) {
  const [variant, setVariantState] = useState<LandingVariant>(defaultVariant || getInitialVariant);

  const isV2 = variant === 'v2';

  useEffect(() => {
    localStorage.setItem(VARIANT_STORAGE_KEY, variant);
  }, [variant]);

  const setVariant = useCallback((newVariant: LandingVariant) => {
    setVariantState(newVariant);
  }, []);

  const toggleVariant = useCallback(() => {
    setVariantState(current => current === 'original' ? 'v2' : 'original');
  }, []);

  const value = useMemo(
    () => ({ variant, setVariant, toggleVariant, isV2 }),
    [variant, setVariant, toggleVariant, isV2]
  );

  return (
    <LandingVariantContext.Provider value={value}>
      {children}
    </LandingVariantContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLandingVariant(): LandingVariantContextValue {
  const context = useContext(LandingVariantContext);
  if (!context) {
    throw new Error('useLandingVariant must be used within a LandingVariantProvider');
  }
  return context;
}
